import { makePersisted } from "@solid-primitives/storage";
import { type Component, type JSX, Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

interface ResizableLayoutProps {
  leftContent: JSX.Element;
  rightContent?: JSX.Element;
  showRightPanel?: boolean;
  defaultRightWidth?: number;
  minLeftWidth?: number;
  minRightWidth?: number;
  onResize?: (leftWidth: number, rightWidth: number) => void;
  class?: string;
}

export const ResizableLayout: Component<ResizableLayoutProps> = (props) => {
  const [isDragging, setIsDragging] = createSignal(false);
  const [rightPanelWidth, setRightPanelWidth] = isServer
    ? createSignal(props.defaultRightWidth || 400)
    : makePersisted(createSignal(props.defaultRightWidth || 400), {
        name: "chat_right_panel_width",
        storage: localStorage,
      });

  let containerRef: HTMLDivElement | undefined;
  let dragStartX = 0;
  let dragStartWidth = 0;

  const minLeftWidth = () => props.minLeftWidth || 480;
  const minRightWidth = () => props.minRightWidth || 320;
  const maxRightWidth = () => {
    if (!containerRef) return 800;
    return containerRef.clientWidth - minLeftWidth();
  };

  // Clamp right panel width to valid range
  const clampedRightWidth = () => {
    const width = rightPanelWidth();
    const max = maxRightWidth();
    const min = minRightWidth();
    return Math.max(min, Math.min(max, width));
  };

  // Calculate left width
  const leftWidth = () => {
    if (!props.showRightPanel || !containerRef) {
      return containerRef?.clientWidth || 0;
    }
    return containerRef.clientWidth - clampedRightWidth();
  };

  // Handle mouse down on resize handle - SSR safe
  const handleMouseDown = (e: MouseEvent) => {
    if (isServer) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartX = e.clientX;
    dragStartWidth = rightPanelWidth();

    // Add global event listeners
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Handle mouse move during drag - SSR safe
  const handleMouseMove = (e: MouseEvent) => {
    if (isServer || !isDragging()) return;

    const deltaX = dragStartX - e.clientX; // Inverted because right panel shrinks when moving left
    const newWidth = dragStartWidth + deltaX;
    const clampedWidth = Math.max(minRightWidth(), Math.min(maxRightWidth(), newWidth));

    setRightPanelWidth(clampedWidth);

    // Call resize callback
    props.onResize?.(leftWidth(), clampedWidth);
  };

  // Handle mouse up to end drag - SSR safe
  const handleMouseUp = () => {
    if (isServer) return;
    setIsDragging(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  // Handle window resize - SSR safe
  const handleWindowResize = () => {
    if (isServer) return;
    // Ensure right panel width is still valid after window resize
    const currentWidth = rightPanelWidth();
    const newMaxWidth = maxRightWidth();
    if (currentWidth > newMaxWidth) {
      setRightPanelWidth(newMaxWidth);
    }
  };

  onMount(() => {
    if (isServer) return;
    window.addEventListener("resize", handleWindowResize);
  });

  onCleanup(() => {
    if (isServer) return;
    window.removeEventListener("resize", handleWindowResize);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  });

  // Update callback when widths change
  createEffect(() => {
    if (props.showRightPanel) {
      props.onResize?.(leftWidth(), clampedRightWidth());
    }
  });

  return (
    <div ref={containerRef} class={`flex h-full relative ${props.class || ""}`}>
      {/* Left Pane - Chat Messages */}
      <div
        class="flex-shrink-0 h-full overflow-y-auto"
        style={{
          width: props.showRightPanel ? `${leftWidth()}px` : "100%",
        }}
      >
        {props.leftContent}
      </div>

      {/* Resize Handle */}
      <Show when={props.showRightPanel && props.rightContent}>
        <div
          class={`
            flex-shrink-0 w-1 bg-gray-200 dark:bg-gray-700 cursor-col-resize
            hover:bg-gray-300 dark:hover:bg-gray-600 active:bg-gray-400 dark:active:bg-gray-500
            ${isDragging() ? "bg-gray-400 dark:bg-gray-500" : ""}
          `}
          onMouseDown={handleMouseDown}
        >
          {/* Hover indicator */}
          <div
            class={`
              absolute inset-y-0 -left-1 -right-1 bg-transparent
              hover:bg-gray-300/50 dark:hover:bg-gray-600/50
              ${isDragging() ? "bg-gray-400/50 dark:bg-gray-500/50" : ""}
            `}
          />
        </div>
      </Show>

      {/* Right Pane - Artifacts */}
      <Show when={props.showRightPanel && props.rightContent}>
        <div
          class="flex-shrink-0 h-full relative"
          style={{
            width: `${clampedRightWidth()}px`,
          }}
        >
          {props.rightContent}
        </div>
      </Show>
    </div>
  );
};
